/**
 * Cookie management utilities
 */

/**
 * Set a cookie with optional expiration
 * @param name Cookie name
 * @param value Cookie value
 * @param days Days until expiration (optional)
 */
export const setCookie = (name: string, value: string, days?: number) => {
  let expires = ''
  let expirationDate: Date | null = null

  if (days) {
    expirationDate = new Date()
    expirationDate.setTime(
      expirationDate.getTime() + days * 24 * 60 * 60 * 1000,
    )
    expires = `; expires=${expirationDate.toUTCString()}`
  }

  // Store the expiration timestamp in the cookie value
  const cookieValue = expirationDate
    ? `${value}|${expirationDate.getTime()}`
    : value

  document.cookie = `${name}=${cookieValue}${expires}; path=/`
}

/**
 * Get a cookie value by name
 * @param name Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

/**
 * Delete a cookie by name
 * @param name Cookie name
 */
export const deleteCookie = (name: string) => {
  setCookie(name, '', -1)
}

/**
 * Check if a cookie's expiration is less than 1 day and delete it if true
 * @param name Cookie name
 * @returns boolean indicating if cookie was deleted
 */
export const checkAndDeleteCookieIfExpiringSoon = (name: string): boolean => {
  const nameEQ = `${name}=`
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) {
      const cookieValue = c.substring(nameEQ.length, c.length)
      const [value, expirationTimestamp] = cookieValue.split('|')

      if (expirationTimestamp) {
        const expiresDate = new Date(parseInt(expirationTimestamp))
        const now = new Date()
        const hoursUntilExpiry =
          (expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60)

        // If less than 24 hours until expiry, delete the cookie
        if (hoursUntilExpiry < 24) {
          deleteCookie(name)
          return true
        }
      }
      break
    }
  }
  return false
}
